import { Plus } from "lucide-react";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { QuickAddPreview } from "@/components/quick-add-preview";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useHotkey } from "@/hooks/use-hotkey";
import { useCategories, useStreams, useWallets } from "@/hooks/use-finance";
import { createTransaction, deleteTransaction } from "@/lib/finance";
import { formatUGX } from "@/lib/money";
import { resolveMoney } from "@/lib/money-match";
import { createTask, deleteTask } from "@/lib/planner";
import { parseQuickAdd } from "@/lib/quick-add";
import { formatDay, formatInZone } from "@/lib/time";
import { useUIStore } from "@/stores/ui";

// Saves straight to Dexie + outbox, so capture works offline and never waits
// on the network (quick-add must stay under 5 seconds).
export function QuickAddDialog() {
	const open = useUIStore((s) => s.quickAddOpen);
	const setOpen = useUIStore((s) => s.setQuickAddOpen);
	const [text, setText] = useState("");

	const openDialog = useCallback(() => setOpen(true), [setOpen]);
	useHotkey("q", openDialog);

	const parsed = useMemo(() => parseQuickAdd(text), [text]);
	const wallets = useWallets();
	const categories = useCategories();
	const streams = useStreams();
	const targets = useMemo(
		() =>
			parsed && parsed.kind !== "task"
				? resolveMoney(parsed, {
						wallets: wallets ?? [],
						categories: categories ?? [],
						streams: streams ?? [],
					})
				: null,
		[parsed, wallets, categories, streams],
	);
	const target = targets
		? targets.wallet
			? [targets.wallet.name, targets.category?.name ?? targets.stream?.name]
					.filter(Boolean)
					.join(" · ")
			: "No wallet yet: add one on the Money screen"
		: undefined;
	const canSave = parsed?.kind === "task" ? parsed.title !== "" : !!targets?.wallet;

	function close(next: boolean) {
		setOpen(next);
		if (!next) setText("");
	}

	async function saveMoney() {
		if (!parsed || parsed.kind === "task" || !targets?.wallet) return;
		try {
			const t = await createTransaction({
				kind: parsed.kind,
				amount: parsed.amount,
				wallet_id: targets.wallet.id,
				category_id: targets.category?.id ?? null,
				stream_id: targets.stream?.id ?? null,
				note: parsed.note,
				occurred_at: parsed.occurredAt?.toISOString(),
			});
			close(false);
			toast.success(`${t.kind === "income" ? "Received" : "Spent"} ${formatUGX(t.amount)}`, {
				description: target,
				action: { label: "Undo", onClick: () => void deleteTransaction(t.id) },
			});
		} catch {
			toast.error("Could not save on this device. Try again.");
		}
	}

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		if (!parsed) return;
		if (parsed.kind !== "task") return saveMoney();
		if (!parsed.title) return;
		try {
			const task = await createTask({
				title: parsed.title,
				tags: parsed.tags,
				estimate_min: parsed.estimateMin,
				due_date: parsed.dueDate,
				due_at: parsed.dueAt?.toISOString() ?? null,
				recurrence_rule: parsed.recurrence,
			});
			close(false);
			const where = parsed.dueAt
				? formatInZone(parsed.dueAt, "EEE d MMM, HH:mm")
				: parsed.dueDate
					? formatDay(parsed.dueDate)
					: "Inbox";
			toast.success(`Added: ${task.title}`, {
				description: where === "Inbox" ? "In your Inbox" : `Due ${where}`,
				action: { label: "Undo", onClick: () => void deleteTask(task.id) },
			});
		} catch {
			toast.error("Could not save on this device. Try again.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={close}>
			<DialogContent className="top-[20%] translate-y-0 sm:max-w-lg">
				<form onSubmit={onSubmit} className="grid gap-4">
					<DialogHeader>
						<DialogTitle>Quick add</DialogTitle>
						<DialogDescription>
							Type a task, <code>-15000 lunch momo</code> or <code>+200000 salary bank</code>.
						</DialogDescription>
					</DialogHeader>
					<Input
						autoFocus
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder="Pay rent Fri 5pm #bills 30m"
						aria-label="Quick add"
						className="h-11 text-base"
					/>
					<QuickAddPreview parsed={parsed} target={target} />
					<DialogFooter>
						<Button type="submit" className="h-11 sm:h-9" disabled={!canSave}>
							<Plus /> Add
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

export function QuickAddButton() {
	const setOpen = useUIStore((s) => s.setQuickAddOpen);
	return (
		<Button
			size="icon"
			onClick={() => setOpen(true)}
			aria-label="Quick add (Q)"
			title="Quick add (Q)"
			className="fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 size-14 rounded-full shadow-lg md:right-6 md:bottom-6"
		>
			<Plus className="size-6" />
		</Button>
	);
}
